/**
 * The CE transcript (M32) — printable, one person, one agency, one cycle.
 *
 * Two doors, one page:
 *   /compliance/transcript/:rosterId?agency=…  — staff (RLS)
 *   /me/transcript?agency=…                    — the person, on their phone (key)
 *
 * Every completion Attest certified and every card the agency verified,
 * with the hours each course earned and the NREMT / NCOEMS category they
 * were assigned to, and totals against the level's table. Built to be typed
 * into NREMT or Continuum in minutes; the CSV is the same data for upload.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { participantFetch } from '../lib/participant.js';
import ErrorBanner from '../components/ErrorBanner.jsx';

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
const when = (d) => (d ? DAY.format(new Date(`${d}T00:00:00Z`)) : '—');
const STAMP = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' });
const h = (n) => `${Number(n ?? 0).toFixed(Number(n ?? 0) % 1 === 0 ? 0 : 1)}`;
const STANDING = { current: 'Current', due_soon: 'Due soon', expired: 'Expired', never: 'Not on record' };

export function TranscriptPage({ participant = false }) {
  const { rosterId } = useParams();
  const [params] = useSearchParams();
  const agency = params.get('agency') ?? '';
  const [t, setT] = useState(undefined);
  const [error, setError] = useState(null);

  const base = participant
    ? `/api/v1/checkin/transcript?agency=${encodeURIComponent(agency)}`
    : `/api/v1/compliance/people/${rosterId}/transcript?agency=${encodeURIComponent(agency)}`;
  const fetcher = participant ? participantFetch : apiFetch;

  useEffect(() => {
    fetcher(base)
      .then((d) => {
        setT(d);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setT(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const downloadCsv = async () => {
    // The API sets Content-Disposition; go through fetch so the key /
    // bearer header rides along, then hand the bytes to the browser.
    const { supabase } = await import('../lib/supabase.js');
    const headers = new Headers();
    if (participant) {
      const { readPersonalKey } = await import('../lib/participant.js');
      const k = readPersonalKey();
      if (k) headers.set('x-participant-key', k);
    } else {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) headers.set('Authorization', `Bearer ${data.session.access_token}`);
    }
    const { baseUrl } = await import('../lib/api.js');
    const res = await fetch(`${baseUrl}${base}&format=csv`, { headers });
    if (!res.ok) {
      setError(new Error(`Could not download the CSV (${res.status}).`));
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `CE_transcript_${(t?.person.full_name ?? 'me').replace(/[^A-Za-z0-9]+/g, '_')}_${t?.cycle.start ?? ''}.csv`;
    a.click();
  };

  return (
    <main className={participant ? 'checkin transcript' : 'transcript'}>
      <div className="no-print transcript__bar">
        {participant ? <Link to="/me">← My record</Link> : <Link to="/compliance">← Compliance</Link>}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn--auto" onClick={downloadCsv} disabled={!t}>
          Download CSV
        </button>
        <button type="button" className="btn btn--primary btn--auto" onClick={() => window.print()} disabled={!t}>
          Print / Save as PDF
        </button>
      </div>
      <ErrorBanner error={error} />
      {t === undefined && <p className="muted">Loading…</p>}
      {t && (
        <article className="record transcript__doc">
          <header className="transcript__head">
            <div>
              <h1>Continuing-education transcript</h1>
              <p className="muted">
                {t.provider.name} · {t.cycle.model_label} · cycle {when(t.cycle.start)} – {when(t.cycle.end)}
              </p>
            </div>
            <div className="transcript__who">
              <strong>{t.person.full_name}</strong>
              <br />
              {t.person.external_id ? `A-B Tech ID ${t.person.external_id}` : 'A-B Tech ID not yet confirmed'}
              <br />
              {t.person.agency}
              {t.person.level ? ` · ${t.person.level}` : ''}
              {t.person.lanes?.length ? ` · ${t.person.lanes.join(', ')}` : ''}
            </div>
          </header>

          <section>
            <h2>Hours this cycle by category</h2>
            <table className="schedule__table transcript__table">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col" className="num">
                    Earned
                  </th>
                  <th scope="col" className="num">
                    Required
                  </th>
                  <th scope="col" className="num">
                    Remaining
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.categories.map((c) => (
                  <tr key={c.domain}>
                    <td>{c.label}</td>
                    <td className="num">{h(c.earned)}</td>
                    <td className="num">{c.required ?? '—'}</td>
                    <td className="num">{c.required != null ? h(Math.max(0, c.required - c.earned)) : '—'}</td>
                  </tr>
                ))}
                <tr>
                  <td>Individual</td>
                  <td className="num">{h(t.individual.earned)}</td>
                  <td className="num">{t.individual.required ?? '—'}</td>
                  <td className="num">{t.individual.required != null ? h(Math.max(0, t.individual.required - t.individual.earned)) : '—'}</td>
                </tr>
                <tr className="transcript__total">
                  <th scope="row">Total</th>
                  <td className="num">{h(t.total.earned)}</td>
                  <td className="num">{t.total.required ?? '—'}</td>
                  <td className="num">{t.total.required != null ? h(Math.max(0, t.total.required - t.total.earned)) : '—'}</td>
                </tr>
              </tbody>
            </table>
            {!t.person.level && (
              <p className="muted">No level is on file for this person at {t.person.agency}, so there is no table to measure against.</p>
            )}
          </section>

          {t.requirements.length > 0 && (
            <section>
              <h2>Required by {t.person.agency}</h2>
              <table className="schedule__table transcript__table">
                <thead>
                  <tr>
                    <th scope="col">Course</th>
                    <th scope="col">Every</th>
                    <th scope="col">Last</th>
                    <th scope="col">Due</th>
                    <th scope="col">Standing</th>
                  </tr>
                </thead>
                <tbody>
                  {t.requirements.map((r) => (
                    <tr key={r.rule_id}>
                      <td>
                        <strong>{r.course_code}</strong> {r.course_name}
                        {r.lane ? ` (${r.lane} lane)` : ''}
                      </td>
                      <td>{r.interval_months} mo</td>
                      <td>
                        {r.last_completed_at ? when(r.last_completed_at.slice(0, 10)) : '—'}
                        {r.last_source === 'card' ? ' (card)' : ''}
                      </td>
                      <td>{r.due_at ? when(r.due_at.slice(0, 10)) : '—'}</td>
                      <td>{STANDING[r.standing] ?? r.standing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section>
            <h2>Completions</h2>
            <p className="muted transcript__note">
              <em>Attest (certified)</em> means the instructor certified attendance and the completion was awarded in Attest.{' '}
              <em>Card (verified)</em> means a card from another provider was checked by {t.person.agency}. Hours are credited
              up to the National Registry's maximum for the course and assigned to the eligible category with the most room.
              Rows outside the cycle are listed for the record and count for nothing here.
            </p>
            <div className="schedule__scroll">
              <table className="schedule__table transcript__table transcript__rows">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Course</th>
                    <th scope="col">Section / provider</th>
                    <th scope="col">Source</th>
                    <th scope="col" className="num">
                      Hours
                    </th>
                    <th scope="col">Assigned to</th>
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r, i) => (
                    <tr key={i} className={r.in_cycle ? '' : 'transcript__outside'}>
                      <td>{when(r.date)}</td>
                      <td>
                        <strong>{r.course_code ?? '—'}</strong> {r.title}
                      </td>
                      <td>
                        {r.section ? `${r.section} · ` : ''}
                        {r.provider}
                        {r.card_ref ? ` · #${r.card_ref}` : ''}
                      </td>
                      <td>{r.source === 'card' ? 'Card (verified)' : 'Attest (certified)'}</td>
                      <td className="num">
                        {h(r.hours_credited)}
                        {r.capped ? <span className="muted"> of {h(r.hours_claimed)}*</span> : ''}
                      </td>
                      <td>
                        {!r.in_cycle
                          ? 'outside cycle'
                          : r.assigned.length
                            ? r.assigned.map((a) => `${a.label} ${h(a.hours)}`).join(', ') + (r.individual ? `, Individual ${h(r.individual)}` : '')
                            : r.individual
                              ? `Individual ${h(r.individual)}`
                              : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {t.rows.some((r) => r.capped) && (
              <p className="muted transcript__note">* Credited up to the National Registry maximum for that course.</p>
            )}
          </section>

          <footer className="transcript__foot muted">
            Generated by Attest {STAMP.format(new Date(t.generated_at))}. Attendance and completions were certified at the time;
            this transcript reports them and assigns no new credit. No scores or answers are shown or held here.
          </footer>
        </article>
      )}
    </main>
  );
}

export default TranscriptPage;
